import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'

import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { useStreamingData } from 'api/streaming'
import { issueCreatorChatAccess } from 'services/endpoints/streaming'
import { useUserProfile } from 'store'
import { useStreamingStore } from 'store/streaming'

import EndStats from '../EndStats/EndStats'
import LiveChat from '../LiveChat/LiveChat'
import StartModal from '../LiveModal/StartModal/StartModal'
import TipModal from '../LiveModal/TipModal/TipModal'
import ViewersModal from '../LiveModal/ViewersModal/ViewersModal'

export type EndStreamTopTipper = {
  id: string
  username?: string | null
  name?: string | null
  avatar?: string | null
  tip_amount: number
}

export type EndStreamStats = {
  views: number
  total_tips: number
  tips_count: number
  average_viewer_duration: number
  top_tippers: EndStreamTopTipper[]
  real_messages_count: number
  stream_id?: string | null
  save_to_vault?: boolean
}

export interface CreatorChatAccessResponse {
  role: 'creator'
  chat: {
    token: string
    roomArn: string
    sessionExpirationTime: string
    tokenExpirationTime: string
    region: string
  }
}

const BroadcastClient = lazy(async () => {
  const module = await import('components/BroadCastClient')

  return {
    default: module.BroadcastClient
  }
})


export const LiveStreamCreator = () => {
  const navigate = useNavigate()

  const isStreaming = useStreamingStore(state => state.isStreaming)

  const commentsActive = useStreamingStore(state => state.commentsActive)
  const startModalOpen = useStreamingStore(state => state.startModalOpen)
  const viewersModalOpen = useStreamingStore(state => state.viewersModalOpen)
  const tipModalOpen = useStreamingStore(state => state.tipModalOpen)
  const tipModalData = useStreamingStore(state => state.tipModalData)
  const endModalOpen = useStreamingStore(state => state.endModalOpen)
  const closeTipModal = useStreamingStore(state => state.closeTipModal)
  const closeViewersModal = useStreamingStore(state => state.closeViewersModal)
  const setTipModalData = useStreamingStore(state => state.setTipModalData)
  const resetForCreateView = useStreamingStore(s => s.resetForCreateView)
  const streamId = useStreamingStore(state => state.streamId)
  const clearStreamId = useStreamingStore(state => state.clearStreamId)

  const user = useUserProfile()
  const { data } = useStreamingData()

  const [chatReady, setChatReady] = useState(false)
  const [showEndStats, setShowEndStats] = useState(false)
  const [endStats, setEndStats] = useState<EndStreamStats | null>(null)
  const [endedStreamId, setEndedStreamId] = useState<string | null>(null)
  const [endedSaveToVault, setEndedSaveToVault] = useState(true)

  const prevStreamingRef = useRef<boolean>(isStreaming)
  const chatBootstrappedRef = useRef(false)

  useEffect(() => {
    resetForCreateView()
  }, [resetForCreateView])

  const bootstrapCreatorChat = useCallback(async () => {
    if (!user?.username) return
    if (chatBootstrappedRef.current) return

    chatBootstrappedRef.current = true
    let cachedChat: CreatorChatAccessResponse['chat'] | null = null

    try {
      const resp = await issueCreatorChatAccess({ username: user.username })

      cachedChat = resp.chat

      ;(window as any).__IVS_CHAT__ = {
        region: resp.chat.region ?? 'us-west-2',
        roomArn: resp.chat.roomArn,
        tokenProvider: async () => {
          if (cachedChat) {
            const current = cachedChat
            cachedChat = null

            return {
              token: current.token,
              sessionExpirationTime: new Date(current.sessionExpirationTime),
              tokenExpirationTime: new Date(current.tokenExpirationTime),
            }
          }

          const refreshed = await issueCreatorChatAccess({
            username: user.username,
          })

          return {
            token: refreshed.chat.token,
            sessionExpirationTime: new Date(
              refreshed.chat.sessionExpirationTime
            ),
            tokenExpirationTime: new Date(
              refreshed.chat.tokenExpirationTime
            ),
          }
        },
      }

      setChatReady(true)
    } catch (e) {
      console.error('[Creator] IVS chat bootstrap failed', e)
      chatBootstrappedRef.current = false
      setChatReady(false)
      ;(window as any).__IVS_CHAT__ = undefined
    }
  }, [user?.username])

  useEffect(() => {
    const wasStreaming = prevStreamingRef.current

    if (wasStreaming && !isStreaming) {
      setShowEndStats(true)
      setChatReady(false)
      chatBootstrappedRef.current = false
      ;(window as any).__IVS_CHAT__ = undefined
    }

    prevStreamingRef.current = isStreaming
  }, [isStreaming])

  useEffect(() => {
    return () => {
      clearStreamId()
      setChatReady(false)
      chatBootstrappedRef.current = false
      ;(window as any).__IVS_CHAT__ = undefined
    }
  }, [clearStreamId])

  const handleCloseEndStats = () => {
    setShowEndStats(false)
    setEndStats(null)
    setEndedStreamId(null)
    setEndedSaveToVault(true)
    setChatReady(false)
    chatBootstrappedRef.current = false
    ;(window as any).__IVS_CHAT__ = undefined
    clearStreamId()

    if (user?.username) {
      navigate(`/${user.username}`, { replace: true })
    }
  }

  const handleStreamEnded = (stats: EndStreamStats | null) => {
    setEndStats(stats)
    setEndedStreamId(stats?.stream_id ?? null)
    setEndedSaveToVault(stats?.save_to_vault ?? true)
  }

  const streamingReady =
    !!data?.channel?.ingestEndpoint && !!data?.streamKey?.value

  return (
    <Container $backgroundImage={user?.avatar?.url ?? ''}>
    <PlayerContainer>
      <Suspense fallback={null}>
        <BroadcastClient
          ingestEndpoint={data?.channel?.ingestEndpoint ?? ''}
          streamKey={data?.streamKey?.value ?? ''}
          onStreamEnded={handleStreamEnded}
          onBroadcastStarted={bootstrapCreatorChat}
        />
      </Suspense>
    </PlayerContainer>

      {!showEndStats && startModalOpen && !isStreaming && (
        <StartModalContainer $visible={!tipModalOpen}>
          <StartModal streamingReady={streamingReady} />
        </StartModalContainer>
      )}

      {!!isStreaming && (
      <LiveChat
        visible={!!isStreaming && !!commentsActive}
        disabled={endModalOpen}
        streamOwnerId={user?.id}
        streamId={streamId}
        chatReady={chatReady}
      />

      )}
      {showEndStats && (
        <EndStats
          stats={endStats}
          streamId={endedStreamId}
          initialSaveToVault={endedSaveToVault}
          onClose={handleCloseEndStats}
        />
      )}

      {tipModalOpen && (
        <TipModal
          data={tipModalData}
          setTipModalData={setTipModalData}
          onClose={closeTipModal}
        />
      )}

      {viewersModalOpen && (
        <ViewersModal
          onClose={closeViewersModal}
          username={user?.username}
          creatorId={user?.id}
        />
      )}
    </Container>
  )
}

interface IContainerProps {
  $backgroundImage: string
}

const Container = styled.div<IContainerProps>`
  display: flex;
  background-size: 100vw 100vh;
  background-repeat: no-repeat;
  background-position: center;
  justify-content: center;
  align-items: center;
  position: fixed;
  inset: 0;
  background-color: black;

  &::before {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
  }

  @media (max-width: 800px) {
    background-size: auto 100vh;
  }
`

const PlayerContainer = styled.div`
  width: 100%;
  min-height: 200px;
`

interface IStartModalContainerProps {
  $visible: boolean
}

const StartModalContainer = styled.div<IStartModalContainerProps>`
  position: fixed;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;

  visibility: ${props => (props.$visible ? 'visible' : 'hidden')};
  pointer-events: ${props => (props.$visible ? 'auto' : 'none')};

  display: flex;
  justify-content: center;
  align-items: center;
`